'use client';

import React from 'react';
import { Input, Textarea } from '@/components/ui/design-system';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PHONE_COUNTRY_CODE } from '@/lib/config/contactForm';

// Base props shared by all field types
interface BaseFieldProps {
  id: string;
  name: string;
  label: string;
  required?: boolean;
  errors?: FormErrors;
  className?: string;
}

// Define FormErrors type locally now that the hook is removed
export interface FormErrors {
  [key: string]: string[] | undefined;
}

// Text input field props
interface TextFieldProps extends BaseFieldProps {
  type: 'text' | 'email' | 'tel' | 'number';
  defaultValue?: string;
  placeholder?: string;
  pattern?: string;
  maxLength?: number;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onValueChange?: (name: string, value: string) => void;
}

// Select field props
interface SelectFieldProps extends BaseFieldProps {
  defaultValue?: string;
  value?: string;
  placeholder?: string;
  options: { value: string; label: string; disabled?: boolean }[];
  onValueChange?: (name: string, value: string) => void;
}

// Textarea field props
interface TextAreaFieldProps extends BaseFieldProps {
  defaultValue?: string;
  value?: string;
  placeholder?: string;
  rows?: number;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  onValueChange?: (name: string, value: string) => void;
}

/**
 * Text Input Field Component
 * Reusable component for text, email, tel inputs
 */
export const TextField: React.FC<TextFieldProps> = ({
  id,
  name,
  label,
  type,
  defaultValue,
  required,
  errors,
  placeholder,
  pattern,
  maxLength,
  onBlur,
  className,
  onValueChange,
}) => {
  const errorMessage = errors?.[name]?.[0];

  return (
    <div className={className} suppressHydrationWarning>
      <Input
        id={id}
        name={name}
        label={label}
        type={type}
        defaultValue={defaultValue}
        required={required}
        placeholder={placeholder}
        maxLength={maxLength}
        error={errorMessage}
        radius="md"
        autoComplete={name === 'name' ? 'name' : name === 'email' ? 'email' : 'off'}
        autoCorrect="off"
        autoCapitalize="off"
        data-lpignore="true"
        data-1p-ignore="true"
        data-form-type="other"
        data-lastpass-ignore="true"
        suppressHydrationWarning
        className="bg-white dark:bg-gray-900/50 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400 focus-visible:ring-primary focus-visible:ring-2 focus-visible:ring-offset-background"
        aria-invalid={!!errorMessage}
        aria-describedby={errorMessage ? `${id}-error` : undefined}
        aria-required={required}
        onBlur={onBlur}
        onValueChange={(val) => onValueChange?.(name, String(val))}
      />
    </div>
  );
};

/**
 * Phone Field Component with country code
 * Specialized input for phone numbers with country code prefix
 */
export const PhoneField: React.FC<Omit<TextFieldProps, 'pattern'>> = ({
  id,
  name,
  label,
  defaultValue,
  required,
  errors,
  placeholder,
  maxLength,
  onBlur,
  className,
  onValueChange,
}) => {
  const errorMessage = errors?.[name]?.[0];

  return (
    <div className={className} suppressHydrationWarning>
      <Input
        id={id}
        name={name}
        label={label}
        type="tel"
        defaultValue={defaultValue}
        required={required}
        placeholder={placeholder}
        maxLength={maxLength}
        error={errorMessage}
        leftIcon={<span className="text-gray-500 dark:text-gray-400 font-semibold">{PHONE_COUNTRY_CODE}</span>}
        onChange={(e) => {
          const numericValue = e.target.value.replace(/[^0-9]/g, '');
          e.target.value = numericValue;
          onValueChange?.(name, numericValue);
        }}
        radius="md"
        inputMode="tel"
        autoComplete="tel"
        autoCorrect="off"
        autoCapitalize="off"
        data-lpignore="true"
        data-1p-ignore="true"
        data-form-type="other"
        data-lastpass-ignore="true"
        suppressHydrationWarning
        className="pl-12 bg-white dark:bg-gray-900/50 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400 focus-visible:ring-rose-500 dark:focus-visible:ring-white focus-visible:ring-2 focus-visible:ring-offset-background"
        aria-invalid={!!errorMessage}
        aria-describedby={errorMessage ? `${id}-error` : undefined}
        aria-required={required}
        onBlur={onBlur}
      />
    </div>
  );
};

/**
 * Select Field Component
 * Dropdown select field with options
 */
export const SelectField: React.FC<SelectFieldProps> = ({
  id,
  name,
  label,
  defaultValue,
  value,
  placeholder,
  required,
  errors,
  options,
  onValueChange,
  className,
}) => {
  const errorMessage = errors?.[name]?.[0];

  return (
    <div className={className}>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <Select name={name} value={value || defaultValue} onValueChange={(value) => onValueChange?.(name, value)} required={required}>
        <SelectTrigger 
          id={id} 
          className={errorMessage ? 'border-red-500' : ''} 
          aria-invalid={!!errorMessage}
          aria-describedby={errorMessage ? `${id}-error` : undefined}
          tabIndex={0}
        >
          <SelectValue placeholder={placeholder || `Select ${label}`} />
        </SelectTrigger>
        <SelectContent>
          {options.map(option => (
            <SelectItem key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {errorMessage && <p id={`${id}-error`} className="mt-1 text-sm text-red-500">{errorMessage}</p>}
    </div>
  );
};

/**
 * TextArea Field Component
 * Multi-line text input
 */
export const TextAreaField: React.FC<TextAreaFieldProps> = ({
  id,
  name,
  label,
  defaultValue,
  value,
  required,
  errors,
  placeholder,
  rows = 4,
  onBlur,
  className,
  onValueChange,
}) => {
  const errorMessage = errors?.[name]?.[0];

  return (
    <div className={className} suppressHydrationWarning>
      <Textarea
        id={id}
        name={name}
        label={label}
        value={value || defaultValue}
        required={required}
        placeholder={placeholder}
        rows={rows}
        error={errorMessage}
        radius="md"
        data-lpignore="true"
        data-1p-ignore="true"
        data-form-type="other"
        data-lastpass-ignore="true"
        suppressHydrationWarning
        className="bg-white dark:bg-gray-900/50 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400 focus-visible:ring-primary focus-visible:ring-2 focus-visible:ring-offset-background"
        aria-invalid={!!errorMessage}
        aria-describedby={errorMessage ? `${id}-error` : undefined}
        aria-required={required}
        onBlur={onBlur}
        onChange={(e) => onValueChange?.(name, e.target.value)}
      />
    </div>
  );
}; 